import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Pencil, Trash2, Plus, LogOut, Mail, MailOpen } from "lucide-react";
import api from "@/lib/api";

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [artists, setArtists] = useState([]);
  const [artworks, setArtworks] = useState([]);
  const [events, setEvents] = useState([]);
  const [messages, setMessages] = useState([]);
  const [dialog, setDialog] = useState({ open: false, type: null, item: null });

  const load = useCallback(async () => {
    try {
      const [a, w, e, m] = await Promise.all([
        api.get("/artists"), api.get("/artworks"), api.get("/events"), api.get("/admin/messages"),
      ]);
      setArtists(a.data); setArtworks(w.data); setEvents(e.data); setMessages(m.data);
    } catch (err) {
      if (err.response?.status === 401 || err.response?.status === 403) navigate("/admin");
    }
  }, [navigate]);

  useEffect(() => { load(); }, [load]);

  const logout = async () => {
    await api.post("/auth/logout");
    navigate("/admin");
  };

  const openCreate = (type) => setDialog({ open: true, type, item: null });
  const openEdit = (type, item) => setDialog({ open: true, type, item });
  const closeDialog = () => setDialog({ open: false, type: null, item: null });

  const handleDelete = async (type, id) => {
    if (!window.confirm("Delete this item?")) return;
    await api.delete(`/admin/${type}/${id}`);
    load();
  };

  const markRead = async (id) => {
    await api.put(`/admin/messages/${id}/read`);
    load();
  };

  return (
    <div className="min-h-screen bg-[#F8F9FA]" data-testid="admin-dashboard">
      {/* Header */}
      <div className="bg-[#0A1128] text-white px-6 py-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <img src="https://customer-assets.emergentagent.com/job_fine-art-frame/artifacts/ygkfc1mx_Untitled%20design%20(42).png" alt="Rain Boy Fine Art" className="h-7 brightness-0 invert" />
          <span className="text-white/50 text-sm">&mdash; Admin</span>
        </div>
        <button onClick={logout} className="flex items-center gap-2 text-sm text-white/70 hover:text-white" data-testid="admin-logout">
          <LogOut size={16} /> Logout
        </button>
      </div>

      <div className="max-w-6xl mx-auto px-6 py-8">
        <Tabs defaultValue="artists" className="w-full">
          <TabsList className="mb-8 bg-white border border-[#0A1128]/10 rounded-none h-auto p-1">
            <TabsTrigger value="artists" className="rounded-none text-xs uppercase tracking-[0.15em] data-[state=active]:bg-[#0A1128] data-[state=active]:text-white" data-testid="tab-artists">Artists ({artists.length})</TabsTrigger>
            <TabsTrigger value="artworks" className="rounded-none text-xs uppercase tracking-[0.15em] data-[state=active]:bg-[#0A1128] data-[state=active]:text-white" data-testid="tab-artworks">Artworks ({artworks.length})</TabsTrigger>
            <TabsTrigger value="events" className="rounded-none text-xs uppercase tracking-[0.15em] data-[state=active]:bg-[#0A1128] data-[state=active]:text-white" data-testid="tab-events">Events ({events.length})</TabsTrigger>
            <TabsTrigger value="messages" className="rounded-none text-xs uppercase tracking-[0.15em] data-[state=active]:bg-[#0A1128] data-[state=active]:text-white" data-testid="tab-messages">Messages ({messages.filter(m => !m.read).length})</TabsTrigger>
          </TabsList>

          {/* Artists Tab */}
          <TabsContent value="artists">
            <div className="flex justify-between items-center mb-6">
              <h2 className="font-heading text-xl text-[#0A1128]">Artists</h2>
              <button onClick={() => openCreate("artists")} className="flex items-center gap-2 bg-[#0A1128] text-white px-4 py-2 text-xs uppercase tracking-[0.15em] hover:bg-[#2c3e50]" data-testid="add-artist-btn"><Plus size={14} /> Add Artist</button>
            </div>
            <div className="bg-white border border-[#0A1128]/10 divide-y divide-[#0A1128]/10">
              {artists.map((a) => (
                <div key={a.id} className="flex items-center justify-between p-4" data-testid={`admin-artist-${a.id}`}>
                  <div className="flex items-center gap-4">
                    <img src={a.photo} alt={a.name} className="w-12 h-12 object-cover" />
                    <div>
                      <p className="text-sm font-medium text-[#0A1128]">{a.name}</p>
                      <p className="text-xs text-[#8A94A6]">{a.medium}{a.featured && " (Featured)"}</p>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <button onClick={() => openEdit("artists", a)} className="p-2 text-[#8A94A6] hover:text-[#0A1128]"><Pencil size={14} /></button>
                    <button onClick={() => handleDelete("artists", a.id)} className="p-2 text-[#8A94A6] hover:text-red-500"><Trash2 size={14} /></button>
                  </div>
                </div>
              ))}
            </div>
          </TabsContent>

          {/* Artworks Tab */}
          <TabsContent value="artworks">
            <div className="flex justify-between items-center mb-6">
              <h2 className="font-heading text-xl text-[#0A1128]">Artworks</h2>
              <button onClick={() => openCreate("artworks")} className="flex items-center gap-2 bg-[#0A1128] text-white px-4 py-2 text-xs uppercase tracking-[0.15em] hover:bg-[#2c3e50]" data-testid="add-artwork-btn"><Plus size={14} /> Add Artwork</button>
            </div>
            <div className="bg-white border border-[#0A1128]/10 divide-y divide-[#0A1128]/10">
              {artworks.map((w) => (
                <div key={w.id} className="flex items-center justify-between p-4" data-testid={`admin-artwork-${w.id}`}>
                  <div className="flex items-center gap-4">
                    <img src={w.image} alt={w.title} className="w-12 h-12 object-cover" />
                    <div>
                      <p className="text-sm font-medium text-[#0A1128]">{w.title}</p>
                      <p className="text-xs text-[#8A94A6]">{w.artist_name} &mdash; ${w.price?.toLocaleString()} {w.sold && "(SOLD)"}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {w.featured && <Badge variant="secondary" className="text-[10px] rounded-none">Featured</Badge>}
                    <button onClick={() => openEdit("artworks", w)} className="p-2 text-[#8A94A6] hover:text-[#0A1128]"><Pencil size={14} /></button>
                    <button onClick={() => handleDelete("artworks", w.id)} className="p-2 text-[#8A94A6] hover:text-red-500"><Trash2 size={14} /></button>
                  </div>
                </div>
              ))}
            </div>
          </TabsContent>

          {/* Events Tab */}
          <TabsContent value="events">
            <div className="flex justify-between items-center mb-6">
              <h2 className="font-heading text-xl text-[#0A1128]">Events</h2>
              <button onClick={() => openCreate("events")} className="flex items-center gap-2 bg-[#0A1128] text-white px-4 py-2 text-xs uppercase tracking-[0.15em] hover:bg-[#2c3e50]" data-testid="add-event-btn"><Plus size={14} /> Add Event</button>
            </div>
            <div className="bg-white border border-[#0A1128]/10 divide-y divide-[#0A1128]/10">
              {events.map((e) => (
                <div key={e.id} className="flex items-center justify-between p-4" data-testid={`admin-event-${e.id}`}>
                  <div>
                    <p className="text-sm font-medium text-[#0A1128]">{e.title}</p>
                    <p className="text-xs text-[#8A94A6]">{e.date} {e.time}{e.is_past ? " (Past)" : ""}</p>
                  </div>
                  <div className="flex gap-2">
                    <button onClick={() => openEdit("events", e)} className="p-2 text-[#8A94A6] hover:text-[#0A1128]"><Pencil size={14} /></button>
                    <button onClick={() => handleDelete("events", e.id)} className="p-2 text-[#8A94A6] hover:text-red-500"><Trash2 size={14} /></button>
                  </div>
                </div>
              ))}
            </div>
          </TabsContent>

          {/* Messages Tab */}
          <TabsContent value="messages">
            <h2 className="font-heading text-xl text-[#0A1128] mb-6">Contact Messages</h2>
            <div className="bg-white border border-[#0A1128]/10 divide-y divide-[#0A1128]/10">
              {messages.length === 0 && <p className="p-6 text-center text-[#8A94A6] text-sm">No messages yet.</p>}
              {messages.map((m) => (
                <div key={m.id} className={`p-4 ${m.read ? "opacity-60" : ""}`} data-testid={`admin-message-${m.id}`}>
                  <div className="flex items-start justify-between">
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        {m.read ? <MailOpen size={14} className="text-[#8A94A6]" /> : <Mail size={14} className="text-[#0A1128]" />}
                        <p className="text-sm font-medium text-[#0A1128]">{m.name}</p>
                        <span className="text-xs text-[#8A94A6]">{m.email}</span>
                      </div>
                      <p className="text-sm text-[#0A1128] mb-1">{m.subject}</p>
                      <p className="text-xs text-[#4A5568]">{m.message}</p>
                      <p className="text-[10px] text-[#8A94A6] mt-2">{new Date(m.created_at).toLocaleString()}</p>
                    </div>
                    {!m.read && (
                      <button onClick={() => markRead(m.id)} className="text-xs text-[#8A94A6] hover:text-[#0A1128] shrink-0">Mark Read</button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </TabsContent>
        </Tabs>
      </div>

      {/* Create/Edit Dialog */}
      <EntityDialog
        open={dialog.open}
        type={dialog.type}
        item={dialog.item}
        artists={artists}
        onClose={closeDialog}
        onSave={load}
      />
    </div>
  );
}

function EntityDialog({ open, type, item, artists, onClose, onSave }) {
  const isEdit = !!item;
  const [form, setForm] = useState({});

  useEffect(() => {
    if (item) {
      setForm({ ...item });
    } else {
      setForm(getDefaults(type));
    }
  }, [item, type, open]);

  const update = (field) => (e) => {
    const val = e.target.type === "checkbox" ? e.target.checked : e.target.value;
    setForm({ ...form, [field]: val });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const payload = buildPayload(type, form);
    if (isEdit) {
      await api.put(`/admin/${type}/${item.id}`, payload);
    } else {
      await api.post(`/admin/${type}`, payload);
    }
    onSave();
    onClose();
  };

  if (!type) return null;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-lg rounded-none border-[#0A1128]/10 max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-heading text-lg">{isEdit ? "Edit" : "Create"} {type.slice(0, -1)}</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4 mt-4">
          {type === "artists" && (
            <>
              <Field label="Name" value={form.name || ""} onChange={update("name")} required />
              <Field label="Photo URL" value={form.photo || ""} onChange={update("photo")} />
              <Field label="Medium" value={form.medium || ""} onChange={update("medium")} />
              <div>
                <Label className="text-xs uppercase tracking-[0.1em] text-[#8A94A6] mb-1.5 block">Bio</Label>
                <Textarea value={form.bio || ""} onChange={update("bio")} rows={4} className="rounded-none text-sm" />
              </div>
              <Check label="Featured" checked={form.featured || false} onChange={update("featured")} />
            </>
          )}
          {type === "artworks" && (
            <>
              <Field label="Title" value={form.title || ""} onChange={update("title")} required />
              <div>
                <Label className="text-xs uppercase tracking-[0.1em] text-[#8A94A6] mb-1.5 block">Artist</Label>
                <select
                  value={form.artist_id || ""}
                  onChange={update("artist_id")}
                  className="w-full border border-[#0A1128]/15 px-3 py-2 text-sm"
                  required
                >
                  <option value="">Select artist...</option>
                  {artists.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
                </select>
              </div>
              <Field label="Image URL" value={form.image || ""} onChange={update("image")} />
              <div>
                <Label className="text-xs uppercase tracking-[0.1em] text-[#8A94A6] mb-1.5 block">Description</Label>
                <Textarea value={form.description || ""} onChange={update("description")} rows={3} className="rounded-none text-sm" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <Field label="Dimensions" value={form.dimensions || ""} onChange={update("dimensions")} />
                <Field label="Price" value={form.price || ""} onChange={update("price")} type="number" />
              </div>
              <div className="flex gap-6">
                <Check label="Sold" checked={form.sold || false} onChange={update("sold")} />
                <Check label="Featured" checked={form.featured || false} onChange={update("featured")} />
              </div>
            </>
          )}
          {type === "events" && (
            <>
              <Field label="Title" value={form.title || ""} onChange={update("title")} required />
              <div className="grid grid-cols-2 gap-4">
                <Field label="Date" value={form.date || ""} onChange={update("date")} type="date" required />
                <Field label="Time" value={form.time || ""} onChange={update("time")} />
              </div>
              <Field label="End Time" value={form.end_time || ""} onChange={update("end_time")} />
              <Field label="Location" value={form.location || ""} onChange={update("location")} />
              <Field label="Image URL" value={form.image || ""} onChange={update("image")} />
              <div>
                <Label className="text-xs uppercase tracking-[0.1em] text-[#8A94A6] mb-1.5 block">Description</Label>
                <Textarea value={form.description || ""} onChange={update("description")} rows={2} className="rounded-none text-sm" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-[0.1em] text-[#8A94A6] mb-1.5 block">Full Description</Label>
                <Textarea value={form.full_description || ""} onChange={update("full_description")} rows={4} className="rounded-none text-sm" />
              </div>
              <Field label="RSVP Link" value={form.rsvp_link || ""} onChange={update("rsvp_link")} />
              <Check label="Past Event" checked={form.is_past || false} onChange={update("is_past")} />
            </>
          )}
          <div className="flex gap-3 pt-2">
            <button type="submit" className="bg-[#0A1128] text-white px-6 py-2.5 text-xs uppercase tracking-[0.15em] hover:bg-[#2c3e50]" data-testid="entity-save-btn">
              {isEdit ? "Update" : "Create"}
            </button>
            <button type="button" onClick={onClose} className="border border-[#0A1128]/20 px-6 py-2.5 text-xs uppercase tracking-[0.15em] text-[#4A5568] hover:bg-[#F8F9FA]">
              Cancel
            </button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, value, onChange, type = "text", required = false }) {
  return (
    <div>
      <Label className="text-xs uppercase tracking-[0.1em] text-[#8A94A6] mb-1.5 block">{label}</Label>
      <Input value={value} onChange={onChange} type={type} required={required} className="rounded-none text-sm border-[#0A1128]/15" />
    </div>
  );
}

function Check({ label, checked, onChange }) {
  return (
    <label className="flex items-center gap-2 text-sm text-[#4A5568] cursor-pointer">
      <input type="checkbox" checked={checked} onChange={onChange} className="accent-[#0A1128]" />
      {label}
    </label>
  );
}

function getDefaults(type) {
  if (type === "artists") return { name: "", photo: "", bio: "", medium: "", featured: false };
  if (type === "artworks") return { title: "", artist_id: "", image: "", description: "", dimensions: "", price: 0, sold: false, featured: false };
  if (type === "events") return { title: "", date: "", time: "", end_time: "", description: "", full_description: "", image: "", location: "", rsvp_link: "", is_past: false };
  return {};
}

function buildPayload(type, form) {
  if (type === "artists") return { name: form.name, photo: form.photo, bio: form.bio, medium: form.medium, featured: !!form.featured };
  if (type === "artworks") return { title: form.title, artist_id: form.artist_id, image: form.image, description: form.description, dimensions: form.dimensions, price: parseFloat(form.price) || 0, sold: !!form.sold, featured: !!form.featured };
  if (type === "events") return { title: form.title, date: form.date, time: form.time, end_time: form.end_time, description: form.description, full_description: form.full_description, image: form.image, location: form.location, rsvp_link: form.rsvp_link, is_past: !!form.is_past };
  return form;
}
